import React from 'react';
import { Batiment } from '../../../../shared/types/game';
import { BUILDING_INTERVENTION_SPAWN_RADIUS_KM } from '../../../../shared/constants/gameMap';
import { useBuildingCapacity } from './useBuildingCapacity';

interface BuildingInfoTabProps {
  building: Batiment;
  address?: string;
  calculatedLevel: number;
  totalCapacity: number;
  totalVehiclesCapacity: number;
  timeRemainingPersonnel: number | null;
  timeRemainingVehicules: number | null;
  handleStartAugmentation: (type: 'personnel' | 'vehicules', places?: number) => void;
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
}

const BuildingInfoTab: React.FC<BuildingInfoTabProps> = ({
  building,
  address,
  calculatedLevel,
  totalCapacity,
  totalVehiclesCapacity,
  timeRemainingPersonnel,
  timeRemainingVehicules,
  handleStartAugmentation,
  onBuildingUpdate
}) => {
  const {
    formatTimeRemaining,
    BASE_CAPACITY_PER_LEVEL,
    CAPACITY_INCREASE_PER_LEVEL,
    CAPACITY_PER_AUGMENTATION,
    XP_REQUIS_PAR_NIVEAU
  } = useBuildingCapacity(building, () => {}, undefined);

  return (
    <div className="space-y-6">
      {/* Statut et niveau */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`px-4 py-2 rounded-lg text-sm font-medium ${
            building.actif
              ? 'bg-green-500/20 text-green-400 border border-green-500/50'
              : 'bg-gray-700 text-gray-400 border border-gray-600'
          }`}>
            {building.actif ? '✓ Actif' : '✗ Inactif'}
          </span>
          {onBuildingUpdate && (
            <button
              onClick={() => {
                const updated = {
                  ...building,
                  actif: !building.actif,
                  updated_at: new Date().toISOString()
                };
                onBuildingUpdate(updated);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                building.actif
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              title={building.actif ? 'Désactiver le bâtiment' : 'Activer le bâtiment'}
            >
              {building.actif ? 'Désactiver' : 'Activer'}
            </button>
          )}
        </div>
        {building.type !== 'cta' && (
          <p className="text-xs text-gray-500 max-w-md">
            Interventions : secteur {BUILDING_INTERVENTION_SPAWN_RADIUS_KM} km sur la carte + liaison CTA obligatoire.
          </p>
        )}
        <div className="flex items-center gap-3">
          <span className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-500/20 text-blue-400 border border-blue-500/50">
            Niveau {building.niveau}
          </span>
          {/* Affichage de l'XP et progression vers le niveau suivant */}
          <div className="px-4 py-2 rounded-lg text-sm bg-gray-800 border border-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">XP:</span>
              <span className="text-white font-semibold">{building.xp || 0}</span>
              <span className="text-gray-500">/</span>
              <span className="text-gray-400">{(calculatedLevel) * XP_REQUIS_PAR_NIVEAU}</span>
            </div>
            <div className="mt-1 w-full bg-gray-700 rounded-full h-1.5">
              <div 
                className="bg-blue-500 h-1.5 rounded-full transition-all"
                style={{ 
                  width: `${Math.min(100, (((building.xp || 0) % XP_REQUIS_PAR_NIVEAU) / XP_REQUIS_PAR_NIVEAU) * 100)}%` 
                }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Le niveau augmente automatiquement avec les interventions terminées (1 XP par intervention)
            </p>
          </div>
        </div>
      </div>

      {/* Localisation */}
      {building.localisation && (
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">Localisation</h3>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            {address ? (
              <p className="text-white">{address}</p>
            ) : (
              <p className="text-gray-400">{building.localisation}</p>
            )}
            {building.type !== 'cta' && (
              <p className="text-xs text-blue-200/90 mt-3 leading-relaxed">
                Secteur de génération des interventions : disque de {BUILDING_INTERVENTION_SPAWN_RADIUS_KM} km autour de ce point
                (affiché sur la carte). La génération exige un centre CTA actif, ce site actif, et la prise d'appel
                ouverte dans le menu interventions (à droite).
                {!building.cta_id ? (
                  <span className="block text-amber-300 mt-1 font-medium">
                    Ce bâtiment n’est pas encore rattaché à un CTA — liez-le dans l’onglet CTA du poste.
                  </span>
                ) : null}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Description */}
      {building.description && (
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">Description</h3>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <p className="text-gray-300 whitespace-pre-wrap">{building.description}</p>
          </div>
        </div>
      )}

      {/* Informations de capacité - Masqué pour le CTA */}
      {building.type !== 'cta' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">Capacité totale</h3>
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-2xl font-bold text-white">{building.capacite || 0}</p>
                <p className="text-sm text-gray-400 mt-1">{building.type === 'prison' ? 'Cellules disponibles' : 'Postes disponibles'}</p>
                <div className="mt-2 text-xs text-gray-500">
                  Base (niv. {calculatedLevel}): {BASE_CAPACITY_PER_LEVEL + (calculatedLevel - 1) * CAPACITY_INCREASE_PER_LEVEL}
                  {building.augmentations_capacite_personnel && building.augmentations_capacite_personnel > 0 && (
                    <span> + Augmentations: +{(building.augmentations_capacite_personnel || 0) * CAPACITY_PER_AUGMENTATION}</span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">{building.type === 'prison' ? 'Cellules' : 'Postes'}</h3>
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-2xl font-bold text-white">
                  {building.postes_occupes || 0}/{building.capacite || 0}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {building.postes_occupes || 0} {building.type === 'prison' ? 'occupées' : 'occupés'}, {building.postes_disponibles || 0} disponibles
                </p>
              </div>
            </div>
          </div>

          {/* Augmentations de capacité */}
          <div className="space-y-4">
          {/* Augmentations en cours */}
          {(building.augmentation_personnel_en_cours || building.augmentation_vehicules_en_cours) && (
            <div className="space-y-2">
              {building.augmentation_personnel_en_cours && (
                <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-yellow-400">
                      Augmentation Personnel en cours
                    </h4>
                    {timeRemainingPersonnel !== null && timeRemainingPersonnel > 0 && (
                      <span className="text-yellow-300 font-mono text-sm">
                        {formatTimeRemaining(timeRemainingPersonnel)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-yellow-300">
                    +{building.augmentation_personnel_en_cours.places * CAPACITY_PER_AUGMENTATION} places en cours d'ajout...
                  </p>
                </div>
              )}
              {building.augmentation_vehicules_en_cours && (
                <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-yellow-400">
                      Augmentation Véhicules en cours
                    </h4>
                    {timeRemainingVehicules !== null && timeRemainingVehicules > 0 && (
                      <span className="text-yellow-300 font-mono text-sm">
                        {formatTimeRemaining(timeRemainingVehicules)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-yellow-300">
                    +{building.augmentation_vehicules_en_cours.places * CAPACITY_PER_AUGMENTATION} places en cours d'ajout...
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Augmentations Personnel */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Augmentations Personnel</h3>
              <button
                onClick={() => handleStartAugmentation('personnel', 1)}
                disabled={!!building.augmentation_personnel_en_cours}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  building.augmentation_personnel_en_cours
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
                title={`Ajouter une augmentation de capacité pour le personnel (+3 ${building.type === 'prison' ? 'cellules' : 'postes'})`}
              >
                + Augmentation
              </button>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <p className="text-2xl font-bold text-white">{building.augmentations_capacite_personnel || 0}</p>
              <p className="text-sm text-gray-400 mt-1">
                Augmentations achetées (+{CAPACITY_PER_AUGMENTATION} {building.type === 'prison' ? 'cellules' : 'postes'} chacune)
              </p>
            </div>
          </div>

          {/* Augmentations Véhicules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Augmentations Véhicules</h3>
              <button
                onClick={() => handleStartAugmentation('vehicules', 1)}
                disabled={!!building.augmentation_vehicules_en_cours}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  building.augmentation_vehicules_en_cours
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
                title="Ajouter une augmentation de capacité pour les véhicules (+3 places chacune)"
              >
                + Augmentation
              </button>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <p className="text-2xl font-bold text-white">{building.augmentations_capacite_vehicules || 0}</p>
              <p className="text-sm text-gray-400 mt-1">
                Augmentations achetées (+{CAPACITY_PER_AUGMENTATION} places chacune)
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Capacité véhicules: {building.capacite_vehicules || totalVehiclesCapacity}
              </p>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Coût */}
      {building.cout !== undefined && building.cout > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">Coût</h3>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <p className="text-2xl font-bold text-white">{building.cout.toLocaleString()} €</p>
          </div>
        </div>
      )}

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">Date de création</h3>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <p className="text-white">
              {new Date(building.created_at).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>
        {building.updated_at && (
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">Dernière mise à jour</h3>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <p className="text-white">
                {new Date(building.updated_at).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Section détaillée des cellules (uniquement pour les prisons) */}
      {building.type === 'prison' && (() => {
        const totalCellules = building.capacite || 0;
        const cellulesOccupees = building.postes_occupes || 0;
        const cellulesDisponibles = building.postes_disponibles || 0;

        return (
          <div className="border-t border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Détail des cellules</h3>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
                + Ajouter une cellule
              </button>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-2xl font-bold text-white">{totalCellules}</p>
                <p className="text-sm text-gray-400 mt-1">Total</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-2xl font-bold text-white">{cellulesOccupees}</p>
                <p className="text-sm text-gray-400 mt-1">Occupées</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-2xl font-bold text-white">{cellulesDisponibles}</p>
                <p className="text-sm text-gray-400 mt-1">Disponibles</p>
              </div>
            </div>

            {/* Liste des cellules */}
            {totalCellules === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-lg mb-2">Aucune cellule disponible</p>
                <p className="text-sm">Les cellules seront créées automatiquement avec les augmentations de capacité</p>
              </div>
            ) : (
              <div className="space-y-3">
                {Array.from({ length: totalCellules }, (_, index) => {
                  const celluleNumero = index + 1;
                  const isOccupee = index < cellulesOccupees;
                  
                  return (
                    <div
                      key={celluleNumero}
                      className={`bg-gray-800 border rounded-lg p-4 transition-colors ${
                        isOccupee
                          ? 'border-red-500/50 hover:border-red-500'
                          : 'border-gray-700 hover:border-blue-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-white">
                            Cellule #{celluleNumero}
                          </h4>
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              isOccupee
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-green-500/20 text-green-400'
                            }`}>
                              {isOccupee ? 'Occupée' : 'Disponible'}
                            </span>
                            <span className="text-xs text-gray-400">
                              Capacité: 4 places
                            </span>
                          </div>
                        </div>
                        <button className="ml-4 text-gray-400 hover:text-white transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Section détaillée des chambres (uniquement pour les hôpitaux) */}
      {building.type === 'hopital' && (() => {
        const totalChambres = building.capacite || 0;
        const chambresOccupees = building.postes_occupes || 0;
        const chambresDisponibles = building.postes_disponibles || 0;
        const PLACES_PAR_CHAMBRE = 2; // 2 lits par chambre

        return (
          <div className="border-t border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Détail des chambres</h3>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
                + Ajouter une chambre
              </button>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-2xl font-bold text-white">{totalChambres}</p>
                <p className="text-sm text-gray-400 mt-1">Total</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-2xl font-bold text-white">{chambresOccupees}</p>
                <p className="text-sm text-gray-400 mt-1">Occupées</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-2xl font-bold text-white">{chambresDisponibles}</p>
                <p className="text-sm text-gray-400 mt-1">Disponibles</p>
              </div>
            </div>

            {/* Liste des chambres */}
            {totalChambres === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-lg mb-2">Aucune chambre disponible</p>
                <p className="text-sm">Les chambres seront créées automatiquement avec les augmentations de capacité</p>
              </div>
            ) : (
              <div className="space-y-3">
                {Array.from({ length: totalChambres }, (_, index) => {
                  const chambreNumero = index + 1;
                  const isOccupee = index < chambresOccupees;
                  
                  return (
                    <div
                      key={chambreNumero}
                      className={`bg-gray-800 border rounded-lg p-4 transition-colors ${
                        isOccupee
                          ? 'border-red-500/50 hover:border-red-500'
                          : 'border-gray-700 hover:border-blue-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-white">
                            Chambre #{chambreNumero}
                          </h4>
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              isOccupee
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-green-500/20 text-green-400'
                            }`}>
                              {isOccupee ? 'Occupée' : 'Disponible'}
                            </span>
                            <span className="text-xs text-gray-400">
                              Capacité: {PLACES_PAR_CHAMBRE} lits
                            </span>
                          </div>
                        </div>
                        <button className="ml-4 text-gray-400 hover:text-white transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default BuildingInfoTab;

